/**
 * Checkout Store Actions - Internal
 * @see mobile: module-checkout/src/modules/checkout/stores/checkout-store.actions.ts
 *
 * 100% matching mobile structure
 * DO NOT import this file directly - use checkout-store.public-api.ts instead
 */

import { checkoutStore$, type CheckoutStore } from './checkout.store';
import type { CustomerAddress, Order, ShippingCartAddress } from '../../../types/front.graphql';
import { type AvailableShippingMethod } from '../models/shipping-method.model';
import { type AvailablePaymentMethod } from '../models/payment-method.model';

/**
 * Strip `id` field from an object before persisting to IndexedDB.
 *
 * Legend-State's IndexedDB plugin uses `id` as the keyPath. When a top-level
 * store property (e.g. `selectedShippingAddress`) holds an object that contains
 * its own `id` field (e.g. CustomerAddress.id = 314423), Legend-State stores
 * the row with key = 314423 instead of key = "selectedShippingAddress".
 * On reload, it then sets `checkoutStore$[314423]` instead of
 * `checkoutStore$.selectedShippingAddress`, causing the value to be lost.
 *
 * This helper moves `id` → `customerAddressId` so the keyPath is no longer
 * polluted, while preserving the address identifier for deduplication logic.
 */
const stripIdForPersistence = <T extends Record<string, unknown>>(obj: T | undefined): T | undefined => {
    if (!obj) return undefined;
    const { id, ...rest } = obj;
    if (id !== undefined) {
        return { ...rest, customerAddressId: id } as unknown as T;
    }
    return obj;
};

/**
 * CHECKOUT STORE ACTIONS
 * Internal actions layer - bridge between store and public API
 * DO NOT import this file directly - use checkout-store.public-api.ts instead
 */

export const checkoutStoreActions = {
    // ========================================================================
    // STATE GETTERS - INTERNAL USE ONLY
    // ========================================================================

    getSelectedShippingAddress: (): ShippingCartAddress | undefined => {
        return checkoutStore$.selectedShippingAddress.peek();
    },

    getShippingMethod: (): AvailableShippingMethod | undefined => {
        return checkoutStore$.selectedShippingMethod.peek();
    },

    getSelectedBillingAddress: (): CustomerAddress | undefined => {
        return checkoutStore$.selectedBillingAddress.peek();
    },

    getSelectedPaymentMethod: (): AvailablePaymentMethod | undefined => {
        return checkoutStore$.selectedPaymentMethod.peek();
    },

    getCustomerNotes: (): string | undefined => {
        return checkoutStore$.customerNotes.peek();
    },

    getPlacedOrder: (): Order | undefined => {
        return checkoutStore$.placedOrder.peek();
    },

    getPlacedOrderId: (): string | undefined => {
        const placedOrder = checkoutStore$.placedOrder.peek();
        return placedOrder?.order_number;
    },

    getLastPaymentMethodCode: (): string | undefined => {
        return checkoutStore$.lastPaymentMethodCode.peek();
    },
    // ========================================================================
    // MUTATIONS - INTERNAL USE ONLY
    // ========================================================================

    setSelectedShippingAddress: (address?: ShippingCartAddress): void => {
        checkoutStore$.selectedShippingAddress.set(
            stripIdForPersistence(address as Record<string, unknown> | undefined) as ShippingCartAddress | undefined
        );
    },

    setSelectedShippingMethod: (method?: AvailableShippingMethod): void => {
        checkoutStore$.selectedShippingMethod.set(method);
    },

    setSelectedBillingAddress: (address?: CustomerAddress): void => {
        checkoutStore$.selectedBillingAddress.set(
            stripIdForPersistence(address as Record<string, unknown> | undefined) as CustomerAddress | undefined
        );
    },

    setSelectedPaymentMethod: (paymentMethod?: AvailablePaymentMethod): void => {
        checkoutStore$.selectedPaymentMethod.set(paymentMethod);
    },

    setCustomerNotes: (notes?: string): void => {
        checkoutStore$.customerNotes.set(notes);
    },

    setPlacedOrder: (order?: Order): void => {
        checkoutStore$.placedOrder.set(order);
    },

    setLastPaymentMethodCode: (code?: string): void => {
        checkoutStore$.lastPaymentMethodCode.set(code);
    },

    setUpdatingAddress: (isUpdating: boolean): void => {
        checkoutStore$.isUpdatingAddress.set(isUpdating);
    },

    incrementDiscountVersion: (): void => {
        checkoutStore$.discountVersion.set((checkoutStore$.discountVersion.peek() ?? 0) + 1);
    },

    /**
     * Store available shipping methods received from the SetShippingAddressesOnCart mutation response.
     * This allows useShippingMethods to consume the data directly without a redundant query.
     */
    setAddressShippingMethodsData: (data: CheckoutStore['addressShippingMethodsData']): void => {
        checkoutStore$.addressShippingMethodsData.set(data);
        checkoutStore$.addressShippingMethodsVersion.set(
            (checkoutStore$.addressShippingMethodsVersion.peek() ?? 0) + 1
        );
    },

    /**
     * Get placed order and immediately clear it (consume pattern)
     * This ensures the order is only processed once
     */
    consumePlacedOrder: (): Order | undefined => {
        const placedOrder = checkoutStore$.placedOrder.peek();

        if (placedOrder) {
            // Clear immediately after reading
            checkoutStore$.placedOrder.set(undefined);
        }

        return placedOrder;
    },

    /**
     * Clear placed order without returning it
     */
    clearPlacedOrder: (): void => {
        console.log('[CheckoutStoreActions] Clearing placed order');
        checkoutStore$.placedOrder.set(undefined);
    },

    /**
     * Clear checkout session data after order placement.
     * Clears shipping/billing address, payment method, shipping method,
     * customer notes, and updating flag.
     * PRESERVES placedOrder and lastPaymentMethodCode for the success/failed page.
     *
     * Call this AFTER setPlacedOrder + setLastPaymentMethodCode.
     */
    clearCheckoutSessionData: (): void => {
        console.log('[CheckoutStoreActions] Clearing checkout session data (preserving placed order)');
        checkoutStore$.selectedShippingAddress.set(undefined);
        checkoutStore$.selectedBillingAddress.set(undefined);
        checkoutStore$.selectedPaymentMethod.set(undefined);
        checkoutStore$.selectedShippingMethod.set(undefined);
        checkoutStore$.customerNotes.set(undefined);
        checkoutStore$.isUpdatingAddress.set(false);
        checkoutStore$.discountVersion.set(0);
        checkoutStore$.addressShippingMethodsVersion.set(0);
        checkoutStore$.addressShippingMethodsData.set(undefined);
    },

    /**
     * Reset entire checkout store
     */
    resetCheckoutStore: (): void => {
        console.log('[CheckoutStoreActions] Resetting checkout store');
        // Set each property individually so Legend-State detects changes
        // and syncs them to IndexedDB. Using .set({...}) on the root
        // may not properly clear all persisted keys.
        checkoutStore$.selectedShippingAddress.set(undefined);
        checkoutStore$.selectedBillingAddress.set(undefined);
        checkoutStore$.selectedPaymentMethod.set(undefined);
        checkoutStore$.customerNotes.set(undefined);
        checkoutStore$.placedOrder.set(undefined);
        checkoutStore$.lastPaymentMethodCode.set(undefined);
        checkoutStore$.selectedShippingMethod.set(undefined);
        checkoutStore$.isUpdatingAddress.set(false);
        checkoutStore$.discountVersion.set(0);
        checkoutStore$.addressShippingMethodsVersion.set(0);
        checkoutStore$.addressShippingMethodsData.set(undefined);
    },
} as const;

export type CheckoutStoreActions = typeof checkoutStoreActions;
